import type { Participant } from '@gripp/shared-logic';
import { useTranslation } from 'react-i18next';
import { Text, View } from 'react-native';
import { getInitials } from '../chat/assetChatRow';
import { Avatar } from '../crop-circle/Avatar';
import { spacing, tokens } from '../crop-circle/tokens';
import { LoadingIndicator } from '../loadingIndicator';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type ParticipantsSectionProps = {
  items: Participant[];
  /**
   * The organization line under every row. This is the asset's own
   * workspace name, not a per-account field — Account is a stitch-merged
   * type and has no organization field Pothos can reach (see
   * memory 0035-pothos-root-fields-publish-only-the-pothos). Every row
   * shares the same line because every participant is, by definition, in
   * this one workspace.
   */
  workspaceName?: string;
  isLoading?: boolean;
  isError?: boolean;
  testID?: string;
};

const getParticipantName = (
  account: Participant['account'],
  unknownLabel: string
): string => {
  const fullName = [account.name?.first, account.name?.last]
    .filter(Boolean)
    .join(' ');
  return fullName || unknownLabel;
};

/**
 * The Participants list: initials avatar, name, organization line where
 * there is one. Nothing else — no add chips, no remove control, no invite
 * disclosure, no per-person reason or group badge; that management surface
 * is out of scope for this epic. Reuses the same initials-chip size the chat
 * feed's compact row uses (`extraSmall`, the 24/10 step of the Crop Circle
 * ramp — see `assetChatRow.tsx`), matching the `ChatMembersPanel` reference's
 * own 24px row avatar.
 */
export const ParticipantsSection = ({
  items,
  workspaceName,
  isLoading,
  isError,
  testID,
}: ParticipantsSectionProps) => {
  const { t } = useTranslation();
  const unknownLabel = t('mentions.unknownUser');

  if (isLoading) {
    return (
      <View testID={testID ? `${testID}-loading` : undefined}>
        <LoadingIndicator size={20} />
      </View>
    );
  }

  if (isError) {
    return (
      <Text
        style={styles.message}
        testID={testID ? `${testID}-error` : undefined}
      >
        {t('chat.participants.errorTitle')}
      </Text>
    );
  }

  if (items.length === 0) {
    return (
      <Text
        style={styles.message}
        testID={testID ? `${testID}-empty` : undefined}
      >
        {t('chat.participants.empty')}
      </Text>
    );
  }

  return (
    <View style={styles.list} testID={testID}>
      {items.map(({ account }) => {
        const name = getParticipantName(account, unknownLabel);
        return (
          <View
            key={account.id}
            style={styles.row}
            testID={testID ? `${testID}-row-${account.id}` : undefined}
          >
            <Avatar
              type="initial"
              size="extraSmall"
              shape="rounded"
              tone="muted"
              text={getInitials(name)}
            />
            <View style={styles.textColumn}>
              <Text style={styles.name} numberOfLines={1}>
                {name}
              </Text>
              {workspaceName && (
                <Text style={styles.organization} numberOfLines={1}>
                  {workspaceName}
                </Text>
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  list: {
    gap: spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  textColumn: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    ...tokens.typography.label.mediumEmphasised,
    color: role.textStrong,
  },
  organization: {
    ...tokens.typography.label.small,
    color: role.textSoft,
  },
  message: {
    ...tokens.typography.label.medium,
    color: role.textSoft,
    paddingVertical: spacing.sm,
  },
}));
